import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  id: string;
  title: string;
  tag: string;
  summary: string;
  rating: number;
  image: string;
}

const SLIDES: Slide[] = [
  {
    id: 'ml-01',
    title: '星河彼岸',
    tag: '科幻 · 冒险 · 中国大陆',
    summary: '穿越虫洞寻找失联探测船，在星河彼岸遇见关于人类未来的答案。',
    rating: 9.2,
    image:
      'https://readdy.ai/api/search-image?query=Epic%20cinematic%20space%20backdrop%2C%20lone%20astronaut%20silhouette%20before%20a%20glowing%20green%20spiral%20galaxy%20and%20nebula%2C%20vast%20dark%20universe%2C%20volumetric%20light%2C%20ultra%20detailed%20concept%20art%2C%20widescreen&width=1600&height=900&seq=nc-hero-01&orientation=landscape',
  },
  {
    id: 'tv-01',
    title: '长夜将明',
    tag: '悬疑 · 犯罪 · 中国大陆',
    summary: '尘封十年的悬案重启，退休刑警与年轻检察官联手追凶，越接近真相越危险。',
    rating: 9.3,
    image:
      'https://readdy.ai/api/search-image?query=Atmospheric%20crime%20thriller%20backdrop%2C%20rain%20slicked%20city%20street%20at%20night%20with%20dramatic%20green%20lighting%2C%20lone%20detective%20silhouette%2C%20cinematic%20mood%2C%20widescreen%20composition&width=1600&height=900&seq=nc-hero-02&orientation=landscape',
  },
  {
    id: 'ml-14',
    title: '钢铁纪元',
    tag: '科幻 · 动作 · 美国',
    summary: '最后一台钢铁机甲被唤醒，人类与机械文明的百年之战迎来终局。',
    rating: 8.9,
    image:
      'https://readdy.ai/api/search-image?query=Epic%20sci%20fi%20action%20backdrop%2C%20giant%20mecha%20robot%20standing%20over%20a%20ruined%20futuristic%20city%20at%20sunset%2C%20glowing%20green%20energy%20core%2C%20smoke%20and%20debris%2C%20widescreen%20cinematic&width=1600&height=900&seq=nc-hero-03&orientation=landscape',
  },
  {
    id: 'tv-08',
    title: '权谋之局',
    tag: '古装 · 历史 · 中国大陆',
    summary: '庙堂之上一步错满盘输，年轻谋士用一场权谋之局改写王朝命运。',
    rating: 9.2,
    image:
      'https://readdy.ai/api/search-image?query=Grand%20ancient%20chinese%20palace%20hall%20backdrop%2C%20emperor%20throne%20in%20dim%20candlelit%20room%20with%20green%20bronze%20details%2C%20dramatic%20historical%20cinematic%20lighting%2C%20widescreen&width=1600&height=900&seq=nc-hero-04&orientation=landscape',
  },
  {
    id: 'ml-06',
    title: '时间褶皱',
    tag: '科幻 · 剧情 · 英国',
    summary: '物理学家陷入时间循环，必须在无数次重复中找出改变一切的瞬间。',
    rating: 9.1,
    image:
      'https://readdy.ai/api/search-image?query=Sci%20fi%20time%20loop%20backdrop%2C%20fractured%20glowing%20clock%20faces%20floating%20in%20dark%20void%2C%20green%20time%20particles%2C%20surreal%20abstract%20cinematic%20widescreen%20composition&width=1600&height=900&seq=nc-hero-05&orientation=landscape',
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => {
    setIndex((next + SLIDES.length) % SLIDES.length);
  };

  const active = SLIDES[index];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {SLIDES.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              title={`${slide.title} - 嫩草影院推荐`}
              className="h-full w-full object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />

        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full px-4 md:px-6">
            <div key={active.id} className="animate-fade-up max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-400">
                <i className="ri-fire-fill" />
                本周热播推荐
              </span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-50 md:text-5xl">
                {active.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-300">
                <span className="flex items-center gap-1 font-bold text-accent-500">
                  <i className="ri-star-fill" />
                  {active.rating.toFixed(1)}
                </span>
                <span className="text-foreground-500">|</span>
                <span>{active.tag}</span>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
                {active.summary}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
                >
                  <i className="ri-play-fill text-lg" />
                  观看详情
                </Link>
                <Link
                  to="/category?type=movie"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-900/60 px-6 text-sm font-medium text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  <i className="ri-compass-3-line text-lg" />
                  探索全部影视
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-5 flex items-center justify-center gap-2">
          {SLIDES.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到 ${slide.title}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-8 bg-primary-500' : 'w-2 bg-foreground-500/60 hover:bg-foreground-300'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-100 backdrop-blur transition-colors hover:bg-primary-500 hover:text-background-950 md:flex md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-100 backdrop-blur transition-colors hover:bg-primary-500 hover:text-background-950 md:flex md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      </div>
    </section>
  );
}